import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Notification } from "../types/api";
import { sanitizeHtml } from "../utils/notificationHtml";

interface NotificationDetailModalProps {
  notification: Notification;
  onClose: () => void;
}

const formatTimestamp = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleString();
};

export const NotificationDetailModal: React.FC<NotificationDetailModalProps> = ({
  notification,
  onClose,
}) => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    // Lock body scroll while modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const metadata = notification.metadata as
    | { sender_name?: string; sender_email?: string; audience?: string }
    | undefined;
  const senderName = metadata?.sender_name;
  const senderEmail = metadata?.sender_email;

  // Portaled to document.body: this modal is position:fixed, and the shell's
  // header uses backdrop-blur, which creates a new containing block for fixed
  // descendants in current browsers — without the portal, rendering inside
  // that header would clip/mispositioned the modal instead of covering the
  // viewport.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-8"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl border border-gray-200 max-w-2xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="notif-modal-title"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-100">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-gray-500 mb-1.5">
              {formatTimestamp(notification.created_at)}
            </div>
            <h2
              id="notif-modal-title"
              className="text-xl font-bold text-gray-900 break-words"
            >
              {notification.title}
            </h2>
            {senderName && (
              <p className="text-sm text-gray-600 mt-1">
                From <span className="font-medium text-gray-800">{senderName}</span>
                {senderEmail && (
                  <span className="text-gray-500"> · {senderEmail}</span>
                )}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-1"
            title="Close"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body. Rich broadcasts are sanitized HTML rendered inside a .ql-editor
            wrapper so they look identical to the composer; everything else is
            plain text with newlines preserved. */}
        <div className="px-6 py-5 overflow-y-auto">
          {notification.content_format === "html" ? (
            <div
              className="notif-rich text-gray-800 leading-relaxed text-sm"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(notification.message) }}
            />
          ) : (
            <div className="text-gray-800 whitespace-pre-wrap leading-relaxed text-sm">
              {notification.message}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 rounded-b-lg flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md bg-ink text-white text-sm font-medium hover:bg-ink-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NotificationDetailModal;
